import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ApiService from '../../services/apiService';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

const ParentDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [linkingChild, setLinkingChild] = useState(false);

  useEffect(() => {
    loadChildren();
  }, []);

  const loadChildren = async () => {
    try {
      setLoading(true);
      const token = await currentUser.getIdToken();
      const childrenData = await ApiService.getLinkedChildren(currentUser.uid, token);
      
      // Fetch learning sessions for each child
      const childrenWithSessions = await Promise.all(
        childrenData.map(async (child) => {
          try {
            const sessionsData = await ApiService.getUserLearningSessions(child.userId, token);
            return {
              ...child,
              totalSessions: sessionsData?.learningSessions?.length || 0,
              sessions: sessionsData?.learningSessions || []
            };
          } catch (err) {
            console.error(`Error fetching sessions for child ${child.userId}:`, err);
            return {
              ...child,
              totalSessions: 0,
              sessions: []
            };
          }
        })
      );
      
      setChildren(childrenWithSessions);
    } catch (err) {
      console.error('Error loading children:', err);
      setError('Failed to load children');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkChild = async (e) => {
    e.preventDefault();
    setLinkingChild(true);
    setError(null);

    try {
      const token = await currentUser.getIdToken();
      
      // Validate invite code first
      const validation = await ApiService.validateInviteCode(inviteCode, token);
      console.log('Validation result:', validation);
      
      if (validation?.success && validation?.data?.valid) {
        // Link the child using the invite code
        const linkResult = await ApiService.linkChildByInviteCode(currentUser.uid, inviteCode, token);
        console.log('Link result:', linkResult);
        
        if (linkResult?.success) {
          // Reload children list
          await loadChildren();
          
          // Close modal and reset
          setShowInviteModal(false);
          setInviteCode('');
          setError(null);
        } else {
          setError(linkResult?.message || 'Failed to link child account');
        }
      } else {
        setError(validation?.data?.message || validation?.message || 'Invalid or expired invite code');
      }
    } catch (err) {
      console.error('Error linking child:', err);
      setError(err.response?.data?.message || err.message || 'Failed to link child. Please check the code and try again.');
    } finally {
      setLinkingChild(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Parent Dashboard</h1>
          <p className="text-gray-600 mt-2">Monitor your children's learning progress</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Add Child Button */}
        <div className="mb-6">
          <button
            onClick={() => setShowInviteModal(true)}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium"
          >
            + Link Child Account
          </button>
        </div>

        {/* Linked Children */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Linked Children</h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading children...</p>
            </div>
          ) : children.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">👨‍👩‍👧</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Children Linked</h3>
              <p className="text-gray-600 mb-4">
                Link your children's accounts to monitor their learning progress
              </p>
              <button
                onClick={() => setShowInviteModal(true)}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                Link First Child
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {children.map((child) => {
                // Calculate average stats from sessions
                const avgAttention = child.sessions.length > 0
                  ? Math.round(child.sessions.reduce((sum, s) => sum + (s.attention_percentage || 0), 0) / child.sessions.length)
                  : 0;
                const totalTime = child.sessions.length > 0
                  ? child.sessions.reduce((sum, s) => sum + (s.time_minutes || 0), 0)
                  : 0;

                return (
                  <div key={child.userId} className="bg-white border border-gray-200 rounded-xl shadow-md hover:shadow-xl transition-all p-6">
                    <div className="flex items-center space-x-4 mb-4">
                      {child.photoURL ? (
                        <img
                          src={child.photoURL}
                          alt={child.displayName}
                          className="h-16 w-16 rounded-full object-cover border-2 border-blue-200"
                        />
                      ) : (
                        <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center border-2 border-blue-200">
                          <span className="text-2xl font-bold text-white">
                            {(child.displayName || child.firstName || 'C').charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 text-lg">{child.displayName || 'Student'}</h3>
                        <p className="text-sm text-gray-600">{child.email}</p>
                        {child.role && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                            {child.role}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-4 bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                          <span className="text-sm text-gray-600">Sessions:</span>
                        </div>
                        <span className="font-bold text-gray-900">{child.totalSessions || 0}</span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <span className="text-sm text-gray-600">Avg Attention:</span>
                        </div>
                        <span className="font-bold text-green-600">{avgAttention}%</span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm text-gray-600">Total Time:</span>
                        </div>
                        <span className="font-bold text-purple-600">{totalTime} min</span>
                      </div>

                      <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                        <span className="text-sm text-gray-600">Status:</span>
                        <span className={`font-semibold flex items-center gap-1 ${child.emailVerified ? 'text-green-600' : 'text-orange-600'}`}>
                          {child.emailVerified ? (
                            <>
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Verified
                            </>
                          ) : (
                            'Pending'
                          )}
                        </span>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => navigate(`/dashboard?childId=${child.userId}`)}
                      className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-semibold shadow-md hover:shadow-lg"
                    >
                      View Dashboard
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Invite Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Link Child Account</h3>
              
              <p className="text-gray-600 mb-4">
                Enter the invite code from your child's account to link it to your parent account.
              </p>
              
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}
              
              <form onSubmit={handleLinkChild} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invite Code
                  </label>
                  <input
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    placeholder="Enter invite code"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowInviteModal(false);
                      setInviteCode('');
                      setError(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={linkingChild}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {linkingChild ? 'Linking...' : 'Link Child'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ParentDashboard;

